import { notifications } from '@leprecoins/effector-notifications';
import {
	createSocket,
	isInvalidDataError,
	isWebSocketError,
	SocketError,
	SocketEvent,
	WebSocketError,
} from '@leprecoins/effector-sockets';
import { combine, sample } from 'effector';
import { t } from 'i18next';
import { z } from 'zod';
import { SOCKET_URL } from 'shared/config';
import { $accessToken } from '../auth';

type ErrorPayload = {
	code: number[];
	msg: string;
	error: boolean;
};

const errorPayloadSchema = z.object({
	code: z.array(z.number()),
	msg: z.string(),
	error: z.literal(true),
});

export const isWebsocketErrorWithPayload = (
	error: SocketError,
): error is WebSocketError<ErrorPayload> =>
	isWebSocketError(error) && errorPayloadSchema.safeParse(error.payload).success;

const [socket, createSend, createBaseReceive] = createSocket<ErrorPayload>({
	url: SOCKET_URL,
	transports: ['websocket'],
	auth: combine($accessToken, (token) => ({ token })),
	errors: ['error'],
	checkError: (event): event is SocketEvent<ErrorPayload> =>
		errorPayloadSchema.safeParse(event.payload).success,
});

const createReceive: typeof createBaseReceive = (...args) => {
	const receive = createBaseReceive(...args);

	sample({
		clock: receive.failure,
		fn: (error) => console.error(`${error.errorType}: ${error.explanation}`, error),
	});

	sample({
		clock: receive.failure,
		filter: isWebsocketErrorWithPayload,
		fn: (error) => t(`errors:${error.payload.code[0]}`),
		target: notifications.showError,
	});

	sample({
		clock: receive.failure,
		filter: isInvalidDataError,
		fn: () => t(`errors:invalid-data`),
		target: notifications.showError,
	});

	return receive;
};

export { socket, createSend, createReceive };
